<template>
	<div ref="sticky" class="sticky-wrapper" :class="{ stuck: stickied }">
		<div class="filter-bar">
			<div class="all-games">{{ $t('main.all-games') }}</div>
			<div v-if="filteredFilters.length > 2" class="filter-list">
				<div
					v-for="filter in filteredFilters"
					:key="filter.name"
					:class="{ selected: selectedFilter === filter.name }"
					class="filter"
					@click="setFilter(filter.name)"
				>
					<svgIcon icon-holder-class="filter-logo" :icon-id="getFilterIconId(filter.name)" />
					<span class="title">{{ $t(`main.filters.${filter.name}`) }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';

export default {
	name: 'GameFilter',
	components: {
		svgIcon,
	},
	props: {
		filters: {
			type: Array,
			default: () => [],
		},
		selectedFilter: {
			type: String,
			default: '',
		},
		stickied: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			stickyOffset: 0,
			resizing: false,
			windowWidth: 0,
		};
	},
	computed: {
		filteredFilters() {
			return this.filters.filter((filter) => filter.sortable);
		},
	},
	mounted() {
		this.$nextTick(() => {
			window.addEventListener('scroll', this.handleScroll);
			window.addEventListener('resize', this.handleResize);

			this.handleResize();
		});
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('resize', this.handleResize);
	},
	methods: {
		setFilter(filter) {
			this.$emit('setFilter', filter);
			dataLayer.push({
				event: 'Games Filtered',
				gameFilterLocation: 'ARENA Home',
				filterOption: this.$t(`main.filters.${filter}`, 'en-US'),
			});
		},
		handleResize() {
			const currentWidth = document.documentElement.clientWidth;
			if (this.windowWidth === currentWidth) return;
			this.windowWidth = currentWidth;
			this.resizing = true;
			this.$emit('setSticky', false);
			window.scrollTo(0, 0);
			setTimeout(() => {
				this.stickyOffset = this.$refs.sticky.offsetTop - 56;
				this.resizing = false;
				this.handleScroll();
			}, 100);
		},
		handleScroll() {
			if (this.resizing) return;
			this.$emit('setSticky', window.pageYOffset >= this.stickyOffset);
		},
		getFilterIconId(filterName) {
			let id = 'icon-';
			switch (filterName) {
				case 'all':
					id += 'btn-all';
					break;
				case 'pc':
					id += 'ext-windows';
					break;
				case 'xbox':
					id += 'ext-xbox';
					break;
				case 'psn':
					id += 'psn';
					break;
				case 'ios':
					id += 'apple';
					break;
				case 'android':
					id += 'ext-android';
					break;
			}
			return id;
		},
	},
};
</script>

<style lang="less" scoped>
.sticky-wrapper {
	background-color: #151517;
	height: 75px;
	margin-top: 25px;
	width: 100%;
	&.stuck {
		margin: 0;
		position: fixed;
		top: 56px;
		z-index: 1;
	}
	.filter-bar {
		align-items: center;
		display: flex;
		height: 100%;
		justify-content: space-between;
		margin: auto;
		width: 335px;
		.all-games {
			color: white;
			font-family: Roboto;
			font-size: 20.5px;
			letter-spacing: -0.41px;
			line-height: 1.46;
		}
		.filter-list {
			display: flex;
			height: 30px;
			.filter {
				align-items: center;
				background-color: #151517;
				border: 1px solid rgba(255, 255, 255, 0.4);
				border-radius: 15px;
				color: #7e7e7f;
				cursor: pointer;
				display: flex;
				font-size: 11.5px;
				height: 100%;
				justify-content: center;
				letter-spacing: -0.23px;
				line-height: 3.13;
				transition: all 0.2s;
				user-select: none;
				width: 30px;
				.title {
					display: none;
				}
				.filter-logo {
					height: 13px;
					opacity: 0.5;
					transition: all 0.2s;
					width: 13px;
				}
				&:not(:last-child) {
					margin-right: 6px;
				}
				&:hover:not(:active):not(.selected) {
					border: 1px solid white;
					color: white;
					.filter-logo {
						opacity: 1;
					}
				}
				&:active:not(.selected) {
					border: 1px solid rgba(255, 255, 255, 0.7);
					color: rgba(255, 255, 255, 0.7);
					.filter-logo {
						opacity: 0.7;
					}
				}
				&.selected {
					background-color: #0077ff;
					border: 1px solid #0077ff;
					color: white;
					cursor: default;
					.filter-logo {
						opacity: 1;
					}
				}
			}
		}
	}
}

@media (min-width: 548px) {
	.sticky-wrapper {
		.filter-bar {
			width: 507px;
		}
	}
}

@media (min-width: 768px) {
	.sticky-wrapper {
		.filter-bar {
			width: 727px;
			.filter-list {
				.filter {
					padding: 0 10px;
					width: auto;
					&:not(:last-child) {
						margin-right: 10px;
					}
					.title {
						display: block;
						margin-top: 2px;
					}
				}
				.filter-logo {
					margin-right: 5px;
					&.all {
						width: 14px;
					}
					&.android {
						height: 17px;
						width: 15px;
					}
				}
			}
		}
	}
}

@media (min-width: 1024px) {
	.sticky-wrapper {
		.filter-bar {
			width: 766px;
			.filter-list {
				.filter-logo {
					&.xbox {
						height: 14px;
						width: 14px;
					}
					&.ios {
						height: 15px;
						width: 12px;
					}
					&.android {
						height: 15px;
						width: 12px;
					}
				}
			}
		}
	}
}

@media (min-width: 1280px) {
	.sticky-wrapper {
		margin-top: 30px;
		.filter-bar {
			width: 961px;
		}
	}
}

@media (min-width: 1440px) {
	.sticky-wrapper {
		.filter-bar {
			width: 1080px;
		}
	}
}

@media (min-width: 1600px) {
	.sticky-wrapper {
		margin-top: 45px;
		.filter-bar {
			width: 1200px;
		}
	}
}

@media (min-width: 1920px) {
	.sticky-wrapper {
		margin-top: 60px;
		.filter-bar {
			width: 1440px;
		}
	}
}
</style>
